import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, ShieldAlert } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';

/**
 * 8-step illustrated walkthrough for setting up a Justimmo Export API user
 * and turning on the Justimmo API publish-channel on each property.
 *
 * Each step is a collapsible card with a one-liner instruction plus the
 * matching screenshot from `frontend/public/justimmo-guide/`. The whole
 * guide renders collapsed by default so the wizard step doesn't get
 * dominated. Clicking the header expands the full list, or each step
 * can be opened individually.
 *
 * Reused on:
 *   - Onboarding step 2 (ImportStep.js). The first place users hit it
 *   - JustimmoPage settings (post-onboarding re-setup / re-rotate creds)
 */

// Single source of truth for the 8 steps. The image basenames match
// what's in `public/justimmo-guide/`.
const STEPS = [
  { key: 'menu',     image: 'navbar.png' },
  { key: 'exports',  image: 'exporte.png' },
  { key: 'create',   image: 'api_erstellen.png' },
  { key: 'autoAdd',  image: 'einstellung_1.png' },
  { key: 'category', image: 'einstellung_2.png' },
  { key: 'open',     image: 'api_bearbeiten.png' },
  { key: 'creds',    image: 'benutzer_pw.png' },
  { key: 'enable',   image: 'objekt_aktivieren.png' },
];

export default function JustimmoGuide({ defaultOpen = false }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultOpen);
  // Tracks which individual step is open. Setting to null collapses all.
  const [openStep, setOpenStep] = useState(null);

  return (
    <div className="border border-[#1f4d3a]/30 bg-[#f0f3ec] rounded-xl overflow-hidden">
      {/* Header. Toggles the whole guide */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#e9efe7] transition"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#e9efe7]">
            <BookOpen className="w-4 h-4 text-[#1f4d3a]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#23211a]">
              {t('justimmoGuide.title') || 'Schritt-für-Schritt-Anleitung anzeigen'}
            </p>
            <p className="text-xs text-[#6f6a5c]">
              {t('justimmoGuide.subtitle') ||
                '8 Screenshots aus Justimmo. Dauert ca. 3 Minuten.'}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-[#6f6a5c] shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-[#6f6a5c] shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-[#1f4d3a]/20 space-y-2">
          {/* Admin-rights warning sits above step 1 since it gates the
              entire flow. Easier to spot here than at the start of the
              first step's body. */}
          <div className="flex items-start gap-2 mt-3 p-3 rounded-lg bg-[#f6edd8] border border-[#c7a869] text-[#9a6d1f]">
            <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="text-xs">
              {t('justimmoGuide.adminWarning') ||
                'Hinweis: Du brauchst Administrator-Rechte in Justimmo, sonst siehst du den Punkt „Exporte" nicht.'}
            </p>
          </div>

          {STEPS.map((step, i) => {
            const isOpen = openStep === step.key;
            return (
              <div
                key={step.key}
                className={`rounded-lg border transition ${
                  isOpen
                    ? 'border-[#1f4d3a]/50 bg-white'
                    : 'border-[#e3dccb] bg-[#f2ecdf]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenStep(isOpen ? null : step.key)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[#f2ecdf] transition"
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${
                      isOpen
                        ? 'bg-[#1f4d3a] text-white'
                        : 'bg-[#e9e2d2] text-[#6f6a5c]'
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="text-sm text-[#23211a] flex-1">
                    {t(`justimmoGuide.steps.${step.key}.title`) || step.key}
                  </span>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-[#8a8474] shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-[#8a8474] shrink-0" />
                  )}
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1 space-y-3">
                    <p className="text-xs text-[#6f6a5c] leading-relaxed">
                      {t(`justimmoGuide.steps.${step.key}.body`)}
                    </p>
                    <a
                      // Opens the full-resolution screenshot in a new tab
                      // so users can see arrows / labels at full size.
                      href={`/justimmo-guide/${step.image}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-lg overflow-hidden border border-[#d8d0bd] bg-[#f7f3ea] hover:border-[#1f4d3a]/50 transition"
                    >
                      <img
                        src={`/justimmo-guide/${step.image}`}
                        alt={t(`justimmoGuide.steps.${step.key}.title`) || step.key}
                        className="w-full max-h-72 object-contain"
                        loading="lazy"
                      />
                    </a>
                  </div>
                )}
              </div>
            );
          })}

          <p className="text-xs text-[#8a8474] italic pt-2">
            {t('justimmoGuide.footer') ||
              'Klick auf ein Bild, um es in voller Größe in einem neuen Tab zu öffnen.'}
          </p>
        </div>
      )}
    </div>
  );
}
